import * as React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { theme } from "./theme";

import NavigationBar from "./navigation/NavigationBar";
import Footer from "./navigation/Footer";
import TermsOfService from "./navigation/TermsOfService";
import PrivacyPolicy from "./navigation/PrivacyPolicy";

// Pages
import Welcome from "./pages/Welcome";
import Entry from "./pages/auth/Entry";
import ProfileAssessment from "./pages/ProfileAssessment";
import OccasionSelection from "./pages/OccasionSelection";
import PBGtest from "./pages/scores/PBGtest";
import ExtPRStest from "./pages/scores/ExtPRStest";
import EKStest from "./pages/scores/EKStest";
import AvailabilityAssessment from "./pages/scores/AvailabilityAssessment";
import ProfileView from "./pages/Profile";
import Dashboard from "./pages/KontrolScore";
import ScoreFeedback from "./pages/ScoreFeedback";
import ReadinessCheck from "./pages/ReadinessCheck";
import EmployeeScreen from "./pages/EmployeeScreen";
import ActionPlanOverview from "./pages/action_plan/ActionPlanOverview";
import ManagerDashboard from "./pages/ManagerDashboard";
import AdminUserManagement from "./pages/AdminUserManagement";
import AdminCompanyManagement from "./pages/AdminCompanyManagement";
import AdminEventManagement from "./pages/AdminEventManagement";
import AdminFeedbackResponses from "./pages/AdminFeedbackResponses";
import Emergency from "./pages/Emergency";
import AuthCallback from "./pages/auth/AuthCallback";
import EmailConfirmed from "./pages/auth/EmailConfirmed";
import ResetPassword from "./pages/auth/ResetPassword";

// Modules
import ModuleWrapper from "./pages/utils/ModuleWrapper";
import {
  SuppliesModule,
  BackpackModule,
  FirstAidModule,
  DocumentsModule,
  CrisisModule,
  EvacuationModule,
  FitBodyModule,
  MentalStrengthModule,
  CommunityModule,
} from "./pages/action_plan/Modules";

import { I18nProvider } from "./i18n/I18nProvider";
import { fetchEmployeeRoleByAuthUserId, getCurrentSession } from "./api/queries";
import { saveCachedRole } from "./pages/utils/Utils";

const ROUTE_PAGES: Record<string, string> = {
  "/email-confirmed": "EmailConfirmed",
  "/reset-password": "ResetPassword",
  "/terms-and-conditions": "Terms",
  "/privacy-policy": "Privacy",
};

const PAGE_ROUTES: Record<string, string> = {
  EmailConfirmed: "/email-confirmed",
  ResetPassword: "/reset-password",
  Terms: "/terms-and-conditions",
  Privacy: "/privacy-policy",
};

const NON_RESTORABLE_PAGES = new Set([
  "Welcome",
  "Entry",
  "AuthCallback",
  "EmailConfirmed",
  "ResetPassword",
  "Terms",
  "Privacy",
]);

const ROUTED_PATHS = new Set(Object.keys(ROUTE_PAGES));
const ADMIN_RESTORABLE_PAGES = new Set(["Manager", "Profile", "Emergency", "AdminUsers", "AdminCompanies", "AdminEvents", "AdminFeedback"]);


export default function App() {
  const [currentPage, setCurrentPage] = React.useState("Welcome");
  const [isRestoring, setIsRestoring] = React.useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const handlePageChange = React.useCallback((page: string) => {
    setCurrentPage(page);

    if (page === "Welcome" || page === "Entry") {
      localStorage.removeItem("tk.currentPage");
    } else if (!NON_RESTORABLE_PAGES.has(page)) {
      localStorage.setItem("tk.currentPage", page);
    }

    const route = PAGE_ROUTES[page];
    if (route && location.pathname !== route) {
      navigate(route);
    } else if (!route && ROUTED_PATHS.has(location.pathname)) {
      navigate("/");
    }
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  React.useEffect(() => {
    let mounted = true;
    setIsRestoring(true);

    async function restoreSessionAndPage() {
      const { data: { session }} = await getCurrentSession();

      if (!mounted) return;

      const routePage = ROUTE_PAGES[location.pathname];
      if (routePage) {
        setCurrentPage(routePage);
        setIsRestoring(false);
        return;
      }

      // If NOT logged in -> always start at the welcome screen
      if (!session) {
        setCurrentPage("Welcome");
        localStorage.removeItem("tk.currentPage");
        setIsRestoring(false);
        return;
      }

      const { data: employee } = await fetchEmployeeRoleByAuthUserId(session.user.id);

      if (!mounted) return;

      const role = String(employee?.company_role ?? "employee").toLowerCase();
      saveCachedRole(role);

      if (role === "admin") {
        const savedPage = localStorage.getItem("tk.currentPage");
        const adminPage =
          savedPage && ADMIN_RESTORABLE_PAGES.has(savedPage) ? savedPage : "Manager";

        setCurrentPage(adminPage);
        localStorage.setItem("tk.currentPage", adminPage);
        setIsRestoring(false);
        return;
      }

      // If logged in → restore last page or default
      const savedPage = localStorage.getItem("tk.currentPage");

      if (savedPage && savedPage !== "Entry") {
        setCurrentPage(savedPage);
      } else {
        // sensible post-login default
        setCurrentPage("Entry");
      }

      setIsRestoring(false);
    }

    restoreSessionAndPage();
    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  const pages: { [key: string]: JSX.Element } = {
    Welcome: <Welcome handlePageChange={handlePageChange} />,
    Entry: <Entry handlePageChange={handlePageChange} />,
    ReadinessCheck: <ReadinessCheck handlePageChange={handlePageChange} />,
    OccasionSelection: <OccasionSelection handlePageChange={handlePageChange} />,
    ProfileAssessment: <ProfileAssessment handlePageChange={handlePageChange} />,
    PBGtest: <PBGtest handlePageChange={handlePageChange} />,
    ExtPRStest: <ExtPRStest handlePageChange={handlePageChange} />,
    EKStest: <EKStest handlePageChange={handlePageChange} />,
    AvailabilityAssessment: <AvailabilityAssessment handlePageChange={handlePageChange} />,
    Profile: <ProfileView handlePageChange={handlePageChange} />,
    KontrolScore: <Dashboard handlePageChange={handlePageChange} />,
    ScoreFeedback: <ScoreFeedback handlePageChange={handlePageChange} />,
    EmployeeScore: <EmployeeScreen handlePageChange={handlePageChange} />,
    ActionPlan: <ActionPlanOverview handlePageChange={handlePageChange} />,
    AuthCallback: <AuthCallback handlePageChange={handlePageChange} />,
    EmailConfirmed: <EmailConfirmed handlePageChange={handlePageChange} />,
    ResetPassword: <ResetPassword handlePageChange={handlePageChange} />,

    // Modules
    ModuleWrapper: <ModuleWrapper handlePageChange={handlePageChange} moduleOrder={1} />,
    ModuleSupplies: <SuppliesModule handlePageChange={handlePageChange} />,
    ModuleBackpack: <BackpackModule handlePageChange={handlePageChange} />,
    ModuleFirstAid: <FirstAidModule handlePageChange={handlePageChange} />,
    ModuleDocuments: <DocumentsModule handlePageChange={handlePageChange} />,
    ModuleCrisis: <CrisisModule handlePageChange={handlePageChange} />,
    ModuleEvacuation: <EvacuationModule handlePageChange={handlePageChange} />,
    ModuleFitBody: <FitBodyModule handlePageChange={handlePageChange} />,
    ModuleMentalStrength: <MentalStrengthModule handlePageChange={handlePageChange} />,
    ModuleCommunity: <CommunityModule handlePageChange={handlePageChange} />,

    Manager: <ManagerDashboard handlePageChange={handlePageChange} />,
    AdminUsers: <AdminUserManagement handlePageChange={handlePageChange} />,
    AdminCompanies: <AdminCompanyManagement handlePageChange={handlePageChange} />,
    AdminEvents: <AdminEventManagement handlePageChange={handlePageChange} />,
    AdminFeedback: <AdminFeedbackResponses handlePageChange={handlePageChange} />,
    Emergency: <Emergency />,

    Terms: <TermsOfService />,
    Privacy: <PrivacyPolicy />,
  };

  return (
    <ThemeProvider theme={theme}>
      <I18nProvider>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            overflowX: "hidden",
            bgcolor: "#F3F4F6",
          }}
        >
          {(!isRestoring && !["Welcome", "Entry", "ReadinessCheck", "OccasionSelection", "ProfileAssessment", "AuthCallback", "EmailConfirmed", "ResetPassword", "Terms", "Privacy"].includes(currentPage)) && (
            <NavigationBar handlePageChange={handlePageChange} currentPage={currentPage} />
          )}

          <Box sx={{ flex: 1 }}>{isRestoring ? null : pages[currentPage]}</Box>

          {!isRestoring && <Footer handlePageChange={handlePageChange} />}
        </Box>
      </I18nProvider>
    </ThemeProvider>
  );
}
